import { useId, useMemo, useState } from "react";
import { useWidth } from "@/hooks/useWidth";
import { cn } from "@/lib/utils";
import { LOCALE, t } from "@shared/i18n";

export type Series = { key: string; label: string; color: string };
export type Datum = { label: string; sublabel?: string; values: Record<string, number> };

const H = 180;
const PAD = { top: 12, right: 8, bottom: 28, left: 40 };

function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

const compact = (n: number) => new Intl.NumberFormat(LOCALE, { notation: "compact", maximumFractionDigits: 1 }).format(n);

/**
 * Histogramme accessible (1 ou plusieurs séries groupées) :
 * barres ≤ 24 px, extrémités arrondies, grille discrète, info-bulle au survol, table équivalente.
 */
export function BarChart({ data, series, format = (n) => compact(n), title, height = H }: { data: Datum[]; series: Series[]; format?: (n: number) => string; title: string; height?: number }) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const [ref, w] = useWidth<HTMLDivElement>();
  const max = useMemo(() => niceMax(Math.max(0, ...data.flatMap((d) => series.map((s) => d.values[s.key] ?? 0)))), [data, series]);
  const innerW = Math.max(10, w - PAD.left - PAD.right);
  const innerH = height - PAD.top - PAD.bottom;
  const band = innerW / Math.max(1, data.length);
  const barW = Math.min(24, (band * 0.7 - 2 * (series.length - 1)) / series.length);
  const y = (v: number) => PAD.top + innerH - (v / max) * innerH;
  // Graduations sans doublon une fois formatées (ex. 0 / 0,5 / 1 en entiers).
  const ticks = [0, max / 2, max].filter((t, i, arr) => arr.findIndex((x) => format(x) === format(t)) === i);
  const empty = data.every((d) => series.every((s) => !(d.values[s.key] ?? 0)));
  const labelEvery = Math.ceil(data.length / Math.max(1, Math.floor(innerW / 44)));

  return (
    <figure className="relative">
      <figcaption className="sr-only">{title}</figcaption>
      {series.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-4 text-[12px] text-muted">
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5">
              <span className="size-2.5 rounded-sm" style={{ background: s.color }} /> {s.label}
            </span>
          ))}
        </div>
      )}
      <div ref={ref} className="w-full">
        <svg width="100%" height={height} viewBox={`0 0 ${w} ${height}`} role="img" aria-labelledby={`${id}-t`} onMouseLeave={() => setHover(null)}>
          <title id={`${id}-t`}>{title}</title>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={w - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--chart-grid)" strokeWidth={1} />
              <text x={PAD.left - 6} y={y(t)} dy="0.32em" textAnchor="end" fontSize={11} fill="var(--subtle)">
                {format(t)}
              </text>
            </g>
          ))}
          {data.map((d, i) => {
            const x0 = PAD.left + i * band + (band - (barW * series.length + 2 * (series.length - 1))) / 2;
            return (
              <g key={d.label + i}>
                <rect x={PAD.left + i * band} y={PAD.top} width={band} height={innerH} fill={hover === i ? "var(--surface)" : "transparent"} onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={0} aria-label={`${d.label} : ${series.map((s) => `${s.label} ${format(d.values[s.key] ?? 0)}`).join(", ")}`} />
                {series.map((s, j) => {
                  const v = d.values[s.key] ?? 0;
                  const h = Math.max(0, y(0) - y(v));
                  const x = x0 + j * (barW + 2);
                  const r = Math.min(4, h, barW / 2);
                  return h > 0 ? (
                    <path key={s.key} pointerEvents="none" fill={s.color} d={`M${x},${y(0)} V${y(v) + r} Q${x},${y(v)} ${x + r},${y(v)} H${x + barW - r} Q${x + barW},${y(v)} ${x + barW},${y(v) + r} V${y(0)} Z`} />
                  ) : null;
                })}
                {i % labelEvery === 0 && (
                  <text x={PAD.left + i * band + band / 2} y={height - 10} textAnchor="middle" fontSize={11} fill="var(--subtle)">
                    {d.label}
                  </text>
                )}
              </g>
            );
          })}
          <line x1={PAD.left} x2={w - PAD.right} y1={y(0)} y2={y(0)} stroke="var(--border-strong)" strokeWidth={1} />
        </svg>
      </div>
      {empty && <p className="pointer-events-none absolute inset-x-0 top-1/3 text-center text-[13px] text-muted">{t("Aucune donnée sur la période.")}</p>}
      {hover !== null && data[hover] && (
        <div className="pointer-events-none absolute top-0 z-10 glass-pop px-3 py-2 text-[12px]" style={{ left: Math.min(w - 160, Math.max(0, PAD.left + hover * band + band / 2 - 70)) }}>
          <p className="font-medium">{data[hover].sublabel ?? data[hover].label}</p>
          {series.map((s) => (
            <p key={s.key} className="mt-0.5 flex items-center gap-1.5 text-muted">
              <span className="size-2 rounded-sm" style={{ background: s.color }} /> {s.label} : <span className="font-medium text-fg tabular-nums">{format(data[hover].values[s.key] ?? 0)}</span>
            </p>
          ))}
        </div>
      )}
      <button onClick={() => setShowTable((v) => !v)} className="mt-1 text-[11px] text-subtle underline-offset-2 hover:underline">
        {showTable ? t("Masquer le tableau") : t("Afficher les données")}
      </button>
      <table className={cn("mt-2 w-full text-[12px]", !showTable && "sr-only")}>
        <thead>
          <tr className="text-left text-muted">
            <th className="py-1 font-medium">{t("Période")}</th>
            {series.map((s) => (
              <th key={s.key} className="py-1 text-right font-medium">
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((d, i) => (
            <tr key={i} className="border-t border-border">
              <td className="py-1">{d.sublabel ?? d.label}</td>
              {series.map((s) => (
                <td key={s.key} className="py-1 text-right tabular-nums">
                  {format(d.values[s.key] ?? 0)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
